import axios from "axios";
import { apiURL } from "./constants";

// Get customer through API
export async function readCustomer(customerID) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
  // Get request using axios with error handling
  const response = await axios.get(`${apiURL}/api/customers/${customerID}/`,
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true
    }
  ).catch(error => {
    console.error('There was an error!', error);
    throw error;
  });

  return response;
}

// Get all customers
export async function readCustomers(searchField, id, pageSize, pageIndex) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
  // Get request using axios with error handling
  const response = await axios.get(`${apiURL}/api/customers/?deleted=false`
    + "&page=" + pageIndex
    + "&page_size=" + pageSize
    + "&id=" + (id != null ? id : "")
    + "&search=" + (searchField != null ? searchField : ""),
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true
    }
  ).catch(error => {
    console.error('There was an error!', error);
    throw error;
  });

  return response;
}

// Post customer through API
// Parameters
// data: the data of the new customer
export async function postCustomer(data) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
  // POST request using axios with error handling
  const response = await axios.post(`${apiURL}/api/customers/`, data,
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true
    }
  ).catch(error => {
    console.error('There was an error!', error);
    throw error;
  });

  return response;
}

// Put customer through API
// Parameters
// data: the data of the existing customer
export async function putCustomer(data) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
  // PUT request using axios with error handling
  const response = await axios.put(`${apiURL}/api/customers/${data.id}/`, data,
    {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true
    }
  ).catch(error => {
    console.error('There was an error!', error);
    throw error;
  });

  return response;
}

// Delete customer through API
export async function deleteCustomer(customer) {
  let copiedCustomer = Object.assign({}, customer);
  const customerID = copiedCustomer.id;
  copiedCustomer["deleted"] = true;
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
  console.log(copiedCustomer);
  const response = await axios.put(`${apiURL}/api/customers/${customerID}/`, copiedCustomer).catch(error => {
    console.error('There was an error!', error);
    throw error;
  });
  return response;
}

// Send OTP to customer's WhatsApp
// Parameters
// phoneNumber: customer's phone number in E.164 format (e.g., +96895917712)
export async function sendCustomerOTP(phoneNumber) {
  try {
    const response = await axios.post(
      `${apiURL}/api/customer/send-otp/`,
      { phone_number: phoneNumber },
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error sending OTP:', error);
    throw error;
  }
}

// Verify OTP and login customer
// Parameters
// phoneNumber: customer's phone number (must match the one used in send-otp)
// otpCode: 6-digit OTP code received via WhatsApp
export async function verifyCustomerOTP(phoneNumber, otpCode) {
  try {
    const response = await axios.post(
      `${apiURL}/api/customer/verify-otp/`,
      {
        phone_number: phoneNumber,
        otp_code: otpCode
      },
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
}

// Switch account for customer with multiple accounts
// Parameters
// userId: ID of the account to login to
// phoneNumber: customer's phone number for verification (optional but recommended)
export async function switchCustomerAccount(userId, phoneNumber) {
  try {
    const response = await axios.post(
      `${apiURL}/api/customer/switch-account/`,
      {
        user_id: userId,
        phone_number: phoneNumber
      },
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error switching account:', error);
    throw error;
  }
}

// Check profile eligibility for completion
// Returns eligibility status and missing fields
export async function checkProfileEligibility() {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.get(
      `${apiURL}/api/customer/complete-profile/`,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error checking profile eligibility:', error);
    throw error;
  }
}

// Complete customer profile (first time only)
// profileData: firstName, lastName, firstNameAr, lastNameAr, idNumber, email (all required strings)
export async function completeProfile(profileData) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.post(
      `${apiURL}/api/customer/complete-profile/`,
      {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        first_name_ar: profileData.firstNameAr,
        last_name_ar: profileData.lastNameAr,
        id_number: profileData.idNumber,
        email: profileData.email.trim(),
      },
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error completing profile:', error);
    throw error;
  }
}

// Get customer dashboard (profile, shipments summary, financial summary, quick stats)
// Requires JWT. Returns 401 if missing/invalid token.
export async function getCustomerDashboard() {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.get(
      `${apiURL}/api/customer/dashboard/`,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error fetching customer dashboard:', error);
    throw error;
  }
}

/**
 * Get customer shipments list (My Shipments).
 * GET /api/customer/shipments/
 * Params: page, page_size, ordering, status, tracking_number, payment_status, shipper_reference, etc.
 * Returns { count, page_size, page_index, results }.
 */
export async function getCustomerShipments(params = {}) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const query = new URLSearchParams();
    const page = params.page != null ? params.page : 1;
    const page_size = params.page_size != null ? params.page_size : 10;
    const ordering = params.ordering != null ? params.ordering : '-created_at';

    query.set('page', String(page));
    query.set('page_size', String(page_size));
    query.set('ordering', ordering);

    [
      'status', 'tracking_number', 'shipper_reference', 'payment_status',
      'items_description', 'current_location', 'note', 'packages_quantity',
      'packages_quantity_min', 'packages_quantity_max', 'weight_min', 'weight_max',
      'width_min', 'width_max', 'length_min', 'length_max', 'height_min', 'height_max',
      'volumetric_weight_min', 'volumetric_weight_max', 'price_min', 'price_max',
      'paid', 'invoiced', 'created_at_after', 'created_at_before',
      'modified_at_after', 'modified_at_before', 'id', 'id_in', 'location', 'type', 'event_type', 'tags',
      'transaction_date', 'transaction_date_after', 'transaction_date_before',
    ].forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.get(
      `${apiURL}/api/customer/shipments/?${query.toString()}`,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error fetching customer shipments:', error);
    throw error;
  }
}

/**
 * Bulk update receiver details (destination type + location) for customer shipments.
 * POST /api/customer/shipments/bulk-receiver-details/
 * Params: shipment_ids (array), destination_location_type ("home"|"store"|"branch"), destination_location (optional, max 300)
 * Returns: { message, updated_count, destination_location_type, destination_location, shipment_ids_updated, shipment_ids_not_found_or_not_owned, shipment_ids_skipped_due_to_status }
 */
export async function bulkUpdateReceiverDetails(shipmentIds, destinationLocationType, destinationLocation) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const body = {
      shipment_ids: Array.isArray(shipmentIds) ? shipmentIds : [shipmentIds],
      destination_location_type: String(destinationLocationType).toLowerCase(),
    };
    if (destinationLocation !== undefined && destinationLocation !== null) {
      body.destination_location = String(destinationLocation).slice(0, 300);
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.post(
      `${apiURL}/api/customer/shipments/bulk-receiver-details/`,
      body,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      }
    );
    return response;
  } catch (error) {
    console.error('Error bulk updating receiver details:', error);
    throw error;
  }
}

/**
 * Export customer shipments to Excel.
 * GET /api/customer/shipments/export-excel/?... (all filtered) or
 * POST /api/customer/shipments/export-excel/ with body { shipment_ids: [...] } (selected only).
 * Params: optional filters (same as list). Optional shipmentIds: array of ids for selected-only export.
 * Returns: blob (xlsx file).
 */
export async function exportCustomerShipmentsToExcel(params = {}) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const shipmentIds = params.shipment_ids != null
      ? (Array.isArray(params.shipment_ids) ? params.shipment_ids : [params.shipment_ids])
      : null;
    const hasSelectedIds = shipmentIds && shipmentIds.length > 0;

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    if (hasSelectedIds) {
      const response = await axios.post(
        `${apiURL}/api/customer/shipments/export-excel/`,
        { shipment_ids: shipmentIds },
        { responseType: 'blob', headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      return response;
    }

    const query = new URLSearchParams();
    const ordering = params.ordering != null ? params.ordering : '-created_at';
    query.set('ordering', ordering);

    [
      'status', 'tracking_number', 'shipper_reference', 'payment_status',
      'items_description', 'current_location', 'note', 'packages_quantity',
      'packages_quantity_min', 'packages_quantity_max', 'weight_min', 'weight_max',
      'width_min', 'width_max', 'length_min', 'length_max', 'height_min', 'height_max',
      'volumetric_weight_min', 'volumetric_weight_max', 'price_min', 'price_max',
      'paid', 'invoiced', 'created_at_after', 'created_at_before',
      'modified_at_after', 'modified_at_before', 'id', 'id_in', 'type', 'event_type', 'tags',
      'transaction_date', 'transaction_date_after', 'transaction_date_before',
    ].forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });

    if (params.location != null) {
      const loc = params.location;
      if (typeof loc === 'object' && loc && loc.id != null) {
        query.set('location', String(loc.id));
      } else if (typeof loc === 'number' || (typeof loc === 'string' && String(loc).trim() !== '')) {
        query.set('location', String(loc));
      }
    }

    const response = await axios.get(
      `${apiURL}/api/customer/shipments/export-excel/?${query.toString()}`,
      { responseType: 'blob', withCredentials: true }
    );
    return response;
  } catch (error) {
    console.error('Error exporting customer shipments:', error);
    throw error;
  }
}

/**
 * Get customer invoices list (My Invoices).
 * GET /api/customer/invoices/
 * Params: page, page_size, ordering, id, id_in, note, payment_status, paid,
 *         created_at_after, created_at_before, modified_at_after, modified_at_before,
 *         shipment, shipment_in.
 * Returns { count, page_size, page_index, results }.
 */
export async function getCustomerInvoices(params = {}) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const query = new URLSearchParams();
    const page = params.page != null ? params.page : 1;
    const page_size = params.page_size != null ? params.page_size : 10;
    const ordering = params.ordering != null ? params.ordering : '-created_at';

    query.set('page', String(page));
    query.set('page_size', String(page_size));
    query.set('ordering', ordering);

    [
      'id', 'id_in', 'note', 'payment_status', 'paid',
      'created_at_after', 'created_at_before', 'modified_at_after', 'modified_at_before',
      'shipment', 'shipment_in',
    ].forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.get(
      `${apiURL}/api/customer/invoices/?${query.toString()}`,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error fetching customer invoices:', error);
    throw error;
  }
}

/**
 * @typedef {Object} CustomerPayment
 * @property {number} id
 * @property {string|null} payment_method
 * @property {string} amount
 * @property {string|null} transaction_date
 * @property {string} created_at
 */

/**
 * Get customer payments list (My Payments).
 * GET /api/customer/payments/
 * Params: page, page_size, ordering, payment_method, transaction_date,
 *         transaction_date_start, transaction_date_end, start_date, end_date.
 * Returns { count, page_size, page_index, results };
 * each result is a {@link CustomerPayment} plus invoice (full invoice object or null)
 * and shipments (array of full shipment objects). Nested invoice/shipment payments
 * omit invoice and shipments; staff-only fields (e.g. transaction_reference) are never returned.
 */
export async function getCustomerPayments(params = {}) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const query = new URLSearchParams();
    const page = params.page != null ? params.page : 1;
    const page_size = params.page_size != null ? params.page_size : 10;
    const ordering = params.ordering != null ? params.ordering : '-created_at';

    query.set('page', String(page));
    query.set('page_size', String(page_size));
    query.set('ordering', ordering);

    [
      'payment_method', 'transaction_date', 'transaction_date_start', 'transaction_date_end',
      'start_date', 'end_date',
    ].forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.get(
      `${apiURL}/api/customer/payments/?${query.toString()}`,
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      }
    );
    return response;
  } catch (error) {
    console.error('Error fetching customer payments:', error);
    throw error;
  }
}

/**
 * Export customer payments to Excel.
 * GET /api/customer/payments/export-excel/?... (all filtered) or
 * POST /api/customer/payments/export-excel/ with body { payment_ids: [...] } (selected only).
 * Params: optional filters (same as list). Optional payment_ids: array of ids for selected-only export.
 * Returns: blob (xlsx file).
 */
export async function exportCustomerPaymentsToExcel(params = {}) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const paymentIds = params.payment_ids != null
      ? (Array.isArray(params.payment_ids) ? params.payment_ids : [params.payment_ids])
      : null;
    const hasSelectedIds = paymentIds && paymentIds.length > 0;

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    if (hasSelectedIds) {
      const response = await axios.post(
        `${apiURL}/api/customer/payments/export-excel/`,
        { payment_ids: paymentIds },
        { responseType: 'blob', headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      return response;
    }

    const query = new URLSearchParams();
    const ordering = params.ordering != null ? params.ordering : '-created_at';
    query.set('ordering', ordering);

    [
      'payment_method', 'transaction_date', 'transaction_date_start', 'transaction_date_end',
      'start_date', 'end_date',
    ].forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });

    const response = await axios.get(
      `${apiURL}/api/customer/payments/export-excel/?${query.toString()}`,
      { responseType: 'blob', withCredentials: true }
    );
    return response;
  } catch (error) {
    console.error('Error exporting customer payments:', error);
    throw error;
  }
}

/**
 * Regenerate payment links for customer's unpaid invoices.
 * POST /api/customer/invoices/regenerate/
 * Body: { invoices_ids: number[] }
 * Returns: { status, regenerated_invoice_count, message }
 */
export async function regenerateCustomerInvoices(invoiceIds) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }
    const ids = Array.isArray(invoiceIds) ? invoiceIds : [invoiceIds];
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.post(
      `${apiURL}/api/customer/invoices/regenerate/`,
      { invoices_ids: ids },
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
    );
    return response;
  } catch (error) {
    console.error('Error regenerating customer invoices:', error);
    throw error;
  }
}

/**
 * Create a fresh payment link when the customer taps Pay.
 * POST /api/customer/invoices/{invoiceId}/pay/
 * Body: { checkout_id?: number }
 * Returns: { status, checkout, payment_link }
 */
export async function payCustomerInvoiceCheckout(invoiceId, checkoutId = undefined) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const body = checkoutId != null ? { checkout_id: checkoutId } : {};
    const response = await axios.post(
      `${apiURL}/api/customer/invoices/${invoiceId}/pay/`,
      body,
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
    );
    return response;
  } catch (error) {
    console.error('Error creating customer invoice payment link:', error);
    throw error;
  }
}

/**
 * Pay full remaining balance for a customer invoice.
 * Cancels pending split checkouts and returns one checkout for amount_due.
 * POST /api/customer/invoices/{invoiceId}/pay-full/
 * Returns: { status, checkout, payment_link }
 */
export async function payFullCustomerInvoice(invoiceId) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    const response = await axios.post(
      `${apiURL}/api/customer/invoices/${invoiceId}/pay-full/`,
      {},
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
    );
    return response;
  } catch (error) {
    console.error('Error paying full customer invoice:', error);
    throw error;
  }
}

/**
 * Export customer invoices to Excel.
 * GET /api/customer/invoices/export-excel/?... (all filtered) or
 * POST /api/customer/invoices/export-excel/ with body { invoice_ids: [...] } (selected only).
 * Params: optional filters (same as list). Optional invoice_ids: array of ids for selected-only export.
 * Returns: blob (xlsx file).
 */
export async function exportCustomerInvoicesToExcel(params = {}) {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const invoiceIds = params.invoice_ids != null
      ? (Array.isArray(params.invoice_ids) ? params.invoice_ids : [params.invoice_ids])
      : null;
    const hasSelectedIds = invoiceIds && invoiceIds.length > 0;

    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    if (hasSelectedIds) {
      const response = await axios.post(
        `${apiURL}/api/customer/invoices/export-excel/`,
        { invoice_ids: invoiceIds },
        { responseType: 'blob', headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );
      return response;
    }

    const query = new URLSearchParams();
    const ordering = params.ordering != null ? params.ordering : '-created_at';
    query.set('ordering', ordering);

    [
      'id', 'id_in', 'note', 'payment_status', 'paid',
      'created_at_after', 'created_at_before', 'modified_at_after', 'modified_at_before',
      'shipment', 'shipment_in',
    ].forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });

    const response = await axios.get(
      `${apiURL}/api/customer/invoices/export-excel/?${query.toString()}`,
      { responseType: 'blob', withCredentials: true }
    );
    return response;
  } catch (error) {
    console.error('Error exporting customer invoices:', error);
    throw error;
  }
}

/** Staff: list customer self-registration queue (requires customers_approve). */
export async function fetchPendingCustomerRegistrations(params = {}) {
  try {
    axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
    const {
      status = 'pending',
      page = 1,
      page_size = 25,
      pagination = true,
    } = params;
    const response = await axios.get(`${apiURL}/api/customers/pending-registrations/`, {
      params: { status, page, page_size, pagination },
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    console.error('fetchPendingCustomerRegistrations:', error);
    throw error;
  }
}

/**
 * Staff: approve a pending registration (requires customers_approve).
 * @param {number} registration_request_id - Queue row id
 * @param {Record<string, string | null>|null|undefined} [user] - Optional profile patch (same request as approval).
 *   Allowed keys: username, first_name, last_name, first_name_ar, last_name_ar, email, phone_number, id_number
 */
export async function approveCustomerRegistration(registration_request_id, user) {
  try {
    axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
    const body = { registration_request_id };
    if (user && typeof user === 'object' && Object.keys(user).length > 0) {
      body.user = user;
    }
    const response = await axios.post(`${apiURL}/api/customers/approve-registration/`, body, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
    return response;
  } catch (error) {
    console.error('approveCustomerRegistration:', error);
    throw error;
  }
}

/** Staff: activate an existing customer (requires customers_activate). */
export async function activateCustomer(customerId) {
  try {
    axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
    const response = await axios.post(
      `${apiURL}/api/customers/${customerId}/activate/`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      }
    );
    return response;
  } catch (error) {
    console.error('activateCustomer:', error);
    throw error;
  }
}

/** Staff: deactivate an existing customer (requires customers_deactivate). */
export async function deactivateCustomer(customerId) {
  try {
    axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
    const response = await axios.post(
      `${apiURL}/api/customers/${customerId}/deactivate/`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      }
    );
    return response;
  } catch (error) {
    console.error('deactivateCustomer:', error);
    throw error;
  }
}

/** Staff: reject a pending registration (requires customers_approve). */
export async function rejectCustomerRegistration(registration_request_id, resolution_note = '') {
  try {
    axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
    const response = await axios.post(
      `${apiURL}/api/customers/reject-registration/`,
      { registration_request_id, resolution_note },
      {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      }
    );
    return response;
  } catch (error) {
    console.error('rejectCustomerRegistration:', error);
    throw error;
  }
}